import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import DateField from '../Shared/DateField';
import ProceduraFileSection from '../Shared/ProceduraFileSection';
import { FilterPill } from '../Shared/FilterPill';
import { listStaffUsers } from '../../services/auth';
import { listClienti } from '../../services/clienti';
import { listAllProceduri } from '../../services/proceduri';
import { listTasks, createTask, updateTask, deleteTask, uploadTaskFile } from '../../services/tasks';
import TaskModal from '../Shared/TaskModal';
import apiClient from '../../services/apiClient';
import { listProceduraFiles } from '../../services/proceduraFiles';
import './Tracker.css';

// AI-generated litigation documents: [prompt file name, button/modal title]
const LITIGIU_DOC_TYPES = [
  ['contestatie_tribunal', 'Contestatie la Tribunal'],
  ['raspuns_arp',          'Raspuns la Aviz de Refuz (ARP)'],
  ['opozitie',             'Opozitie'],
  ['contestatie_apel',     'Contestatie si Apel'],
  ['anulare',              'Cerere de Anulare'],
  ['decadere',             'Cerere de Decadere'],
];

const TIPURI_LITIGIU = [
  ['contrafacere_marca',  'Contrafacere de marca'],
  ['contrafacere_brevet', 'Contrafacere de brevet'],
  ['contrafacere_dmi',    'Contrafacere de desen sau model'],
  ['anulare_marca',       'Anularea inregistrarii unei marci'],
  ['decadere_marca',      'Decaderea din drepturile asupra marcii'],
  ['anulare_brevet',      'Anularea unui brevet'],
  ['anulare_dmi',         'Anularea unui desen sau model'],
  ['concurenta',          'Concurenta neloiala'],
  ['domeniu',             'Litigiu privind un nume de domeniu'],
  ['autor',               'Drepturi de autor si conexe'],
  ['contract',            'Litigiu contractual — licenta, cesiune, franciza'],
  ['contestatie_osim',    'Contestatie impotriva unei hotarari OSIM'],
  ['vamal',               'Masuri vamale — retinere de marfuri'],
  ['penal',               'Plangere penala sau constituire de parte civila'],
  ['altul',               'Altul'],
];

const POZITII = [
  ['reclamant',       'Reclamant'],
  ['parat',           'Parat'],
  ['oponent',         'Oponent'],
  ['intervenient',    'Intervenient'],
  ['chemat_garantie', 'Chemat in garantie'],
  ['petent',          'Petent / parte civila'],
];

const INSTANTE = [
  ['tb',        'Tribunalul Bucuresti'],
  ['trib_alt',  'Alt tribunal'],
  ['cab',       'Curtea de Apel Bucuresti'],
  ['ca_alta',   'Alta curte de apel'],
  ['iccj',      'Inalta Curte de Casatie si Justitie'],
  ['jud',       'Judecatorie'],
  ['ue',        'Instanta a Uniunii Europene'],
  ['arbitraj',  'Arbitraj / mediere'],
];

const SOLUTII = [
  ['',             '— pe rol —'],
  ['admisa',       'Admisa'],
  ['admisa_partial','Admisa in parte'],
  ['respinsa',     'Respinsa'],
  ['anulata',      'Anulata ca netimbrata sau nemotivata'],
  ['perimata',     'Perimata'],
  ['renuntare',    'Renuntare la judecata'],
  ['tranzactie',   'Tranzactie / hotarare de expedient'],
  ['declinata',    'Declinata'],
];

const FAZE = [
  { cheie:'fond',   label:'Fond' },
  { cheie:'apel',   label:'Apel' },
  { cheie:'recurs', label:'Recurs' },
];

const CONFIG_DEFAULT = { cale_atac_zile: 30, alerta_zile: 14 };

/* ── Helpers ── */
const todayMid = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
const parseISO = (s) => (s ? new Date(s + 'T00:00:00') : null);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const fmtRO = (d) => d ? d.toLocaleDateString('ro-RO', { day:'2-digit', month:'2-digit', year:'numeric' }) : '—';
const daysLeft = (d) => d ? Math.ceil((d - todayMid()) / 86400000) : null;
const fmtLei = (n) => Math.round(Number(n) || 0).toLocaleString('ro-RO') + ' lei';

function estimeazaTimbru(valoare) {
  const v = Number(valoare) || 0;
  if (v <= 0) return 0;
  if (v <= 500)    return Math.max(20, v * 0.08);
  if (v <= 5000)   return 40 + (v - 500) * 0.07;
  if (v <= 25000)  return 355 + (v - 5000) * 0.05;
  if (v <= 50000)  return 1355 + (v - 25000) * 0.03;
  if (v <= 250000) return 2105 + (v - 50000) * 0.02;
  return 6105 + (v - 250000) * 0.01;
}

let _nextId = 0;
const newId = () => `li_${Date.now()}_${_nextId++}`;

function fazaGoala() {
  return { numarDosar:'', instanta:'', dataInregistrare:'', solutie:'', dataPronuntare:'', dataComunicare:'', caleAtacDeclarata: false, dataCaleAtac:'' };
}

export function emptyRecord() {
  return {
    denumire: '',
    client: '',
    adversar: '',
    pozitie: 'reclamant',
    tipLitigiu: 'contrafacere_marca',
    obiect: '',
    drepturi: [],
    dataDeschidere: '',
    beneficiar: '',
    responsabil: [],
    fazaCurenta: 'fond',
    faze: { fond: fazaGoala(), apel: fazaGoala(), recurs: fazaGoala() },
    masuriProvizorii: false, masuriTip:'', masuriData:'', masuriSolutie:'',
    expertizaDispusa: false, expert:'', expertizaObiective:'', expertizaTermenRaport:'', expertizaRaportDepus: false,
    valoare: 0, taxaTimbru: 0, taxaPlatita: false,
    executare: false, executareNumarDosar:'', executareStadiu:'',
    termene: [],
    sarcini: [],
    onorariu: 0, onorariuIncasat: 0,
    cheltuieliAvansate: 0, cheltuieliRecuperate: 0,
    inchis: false,
    observatii: '',
    detaliiInterne: '',
  };
}

function deriveStatus(r, CFG) {
  const azi = todayMid();

  const termeneViitoare = (r.termene || [])
    .map(t => ({ ...t, d: parseISO(t.data) }))
    .filter(t => t.d && t.d >= azi)
    .sort((a, b) => a.d - b.d);
  const urmatorulTermen = termeneViitoare[0] || null;

  const sarciniDeschise = (r.sarcini || [])
    .filter(s => !s.done)
    .map(s => ({ ...s, d: parseISO(s.data) }))
    .filter(s => s.d)
    .sort((a, b) => a.d - b.d);
  const urmatoareaSarcina = sarciniDeschise[0] || null;

  const candidati = [];
  const expTermen = r.expertizaDispusa && !r.expertizaRaportDepus ? parseISO(r.expertizaTermenRaport) : null;
  let termenCaleAtac = null, fazaCaleAtac = null;
  FAZE.forEach(f => {
    const fz = r.faze[f.cheie];
    if (fz && fz.solutie && fz.dataComunicare && !fz.caleAtacDeclarata) {
      const t = addDays(parseISO(fz.dataComunicare), CFG.cale_atac_zile);
      if (!termenCaleAtac || t < termenCaleAtac) { termenCaleAtac = t; fazaCaleAtac = f.label; }
    }
  });
  if (termenCaleAtac) candidati.push({ label:`Termen cale de atac — ${fazaCaleAtac}`, date: termenCaleAtac, sev:'act' });
  if (expTermen) candidati.push({ label:'Termen depunere raport de expertiza', date: expTermen, sev:'watch' });
  if (urmatorulTermen) candidati.push({ label: urmatorulTermen.obiectTermen ? `Termen de judecata — ${urmatorulTermen.obiectTermen}` : 'Termen de judecata', date: urmatorulTermen.d, sev:'watch' });
  if (urmatoareaSarcina) candidati.push({ label: urmatoareaSarcina.text || 'Sarcina interna', date: urmatoareaSarcina.d, sev:'act' });

  let alert;
  if (r.inchis) {
    alert = { label:'Dosar inchis', sev:'done' };
  } else if (candidati.length > 0) {
    candidati.sort((a, b) => {
      const sevOrd = { overdue:-2, act:-1, watch:0, info:1, neutral:2 };
      if (a.sev !== b.sev) return (sevOrd[a.sev] || 0) - (sevOrd[b.sev] || 0);
      return (a.date || 0) - (b.date || 0);
    });
    alert = { ...candidati[0] };
  } else {
    const fazaActiva = FAZE.find(f => r.faze[f.cheie] && !r.faze[f.cheie].solutie) || FAZE[0];
    alert = { label:`Pe rol — ${fazaActiva.label}`, sev:'info' };
  }

  if (alert.date) {
    const dl = daysLeft(alert.date);
    alert.daysLeft = dl;
    if (dl < 0 && alert.sev === 'act') alert.sev = 'overdue';
  }

  return { alert, urmatorulTermen, urmatoareaSarcina, termenCaleAtac, fazaCaleAtac, expTermen };
}

/* ════════════════════════ Component ════════════════════════ */
const STATUS_DOT = { neinceput: '#9ca3af', in_progres: '#2563eb', terminat: '#16a34a' };
const STATUS_LABEL = { neinceput: 'Neinceput', in_progres: 'In progres', terminat: 'Terminat' };

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function DosarInstantaTracker({ data, onChange, procId, clientId, showToast, pendingFiles, onPendingFilesChange }) {
  const [r, setR] = useState(data);
  const [cfg, setCfg] = useState(CONFIG_DEFAULT);
  const [showSetari, setShowSetari] = useState(false);
  const [showFinanciar, setShowFinanciar] = useState(false);
  const [staffUsers, setStaffUsers] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [allProceduri, setAllProceduri] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [taskModal, setTaskModal] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [taskSaving, setTaskSaving] = useState(false);
  const [taskDeleting, setTaskDeleting] = useState(false);
  const [activOpen, setActivOpen]         = useState(false);
  const [activText, setActivText]         = useState('');
  const [activGenerating, setActivGenerating] = useState(false);
  const [juriOpen, setJuriOpen]           = useState(false);
  const [juriText, setJuriText]           = useState('');
  const [juriFiles, setJuriFiles]         = useState([]);
  const [juriSelected, setJuriSelected]   = useState(new Set());
  const [juriGenerating, setJuriGenerating] = useState(false);

  // Generic AI litigation-document modal (contestatie, opozitie, anulare, decadere, etc.)
  const [docModal, setDocModal]         = useState(null); // null | { prompt, title }
  const [docText, setDocText]           = useState('');
  const [docFiles, setDocFiles]         = useState([]);
  const [docSelected, setDocSelected]   = useState(new Set());
  const [docGenerating, setDocGenerating] = useState(false);

  const openDocModal = useCallback((prompt, title) => {
    if (!procId) { showToast && showToast('Salveaza procedura inainte de a genera documentul'); return; }
    setDocText('');
    setDocSelected(new Set());
    setDocFiles([]);
    setDocModal({ prompt, title });
    listProceduraFiles(procId).then(res => setDocFiles(res.files || [])).catch(() => {});
  }, [procId, showToast]);

  const handleGenerateDoc = useCallback(async () => {
    if (!docModal) return;
    setDocGenerating(true);
    try {
      const resp = await apiClient.post(
        '/ai/generate-litigiu-doc/',
        { prompt: docModal.prompt, procedura_id: procId, comentariu: docText, file_ids: [...docSelected] },
        { responseType: 'blob' },
      );
      const toSlug = str => (str || '').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_');
      const clientName = clienti.find(c => c.id === clientId)?.nume || '';
      const filename = `${toSlug(docModal.title)}_${toSlug(clientName)}.docx`.replace(/_+/g, '_');
      const url = URL.createObjectURL(new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setDocModal(null);
      showToast && showToast('Document generat cu succes');
    } catch (e) {
      showToast && showToast('Eroare la generarea documentului');
    } finally {
      setDocGenerating(false);
    }
  }, [docModal, procId, docText, docSelected, clienti, clientId, showToast]);

  const openActivModal = useCallback(() => {
    if (!procId) { showToast && showToast('Salveaza procedura inainte de a genera documentul'); return; }
    setActivText('');
    setActivOpen(true);
  }, [procId, showToast]);

  const handleGenerateActivitati = useCallback(async () => {
    setActivGenerating(true);
    try {
      const resp = await apiClient.post(
        '/ai/generate-docx/',
        { prompt: 'alocare_ore_litigiu', procedura_id: procId, comentariu: activText },
        { responseType: 'blob' },
      );
      const toSlug = str => (str || '').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_');
      const clientName = clienti.find(c => c.id === clientId)?.nume || '';
      const denumire   = r.obiect || '';
      const filename   = `Activitati_Facturabile_${toSlug(denumire)}_${toSlug(clientName)}.docx`.replace(/_+/g, '_');
      const url = URL.createObjectURL(new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setActivOpen(false);
      showToast && showToast('Document generat cu succes');
    } catch (e) {
      showToast && showToast('Eroare la generarea documentului');
    } finally {
      setActivGenerating(false);
    }
  }, [procId, activText, showToast, clienti, clientId, r.obiect]);

  const openJuriModal = useCallback(() => {
    if (!procId) { showToast && showToast('Salveaza procedura inainte de a genera documentul'); return; }
    setJuriText('');
    setJuriSelected(new Set());
    setJuriFiles([]);
    setJuriOpen(true);
    listProceduraFiles(procId).then(res => setJuriFiles(res.files || [])).catch(() => {});
  }, [procId, showToast]);

  const handleGenerateJurisprudenta = useCallback(async () => {
    setJuriGenerating(true);
    try {
      const resp = await apiClient.post(
        '/ai/generate-jurisprudenta/',
        { procedura_id: procId, comentariu: juriText, file_ids: [...juriSelected] },
        { responseType: 'blob' },
      );
      const toSlug = str => (str || '').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_');
      const clientName = clienti.find(c => c.id === clientId)?.nume || '';
      const tip = r.tipLitigiu || 'litigiu';
      const filename = `Jurisprudenta_${toSlug(tip)}_${toSlug(clientName)}.docx`.replace(/_+/g, '_');
      const url = URL.createObjectURL(new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setJuriOpen(false);
      showToast && showToast('Jurisprudență generată cu succes');
    } catch (e) {
      showToast && showToast('Eroare la generarea jurisprudenței');
    } finally {
      setJuriGenerating(false);
    }
  }, [procId, juriText, juriSelected, clienti, clientId, r.tipLitigiu, showToast]);

  useEffect(() => {
    listStaffUsers().then(res => setStaffUsers(res.users || [])).catch(() => {});
    listClienti().then(res => setClienti(res.clients || [])).catch(() => {});
    listAllProceduri([]).then(res => setAllProceduri(res.proceduri || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!procId) return;
    listTasks().then(res => {
      const all = res.tasks || [];
      setTasks(all.filter(t => (t.proceduri_ids || []).includes(procId)));
    }).catch(() => {});
  }, [procId]);

  const handleTaskSave = async (form, pendingFiles = []) => {
    setTaskSaving(true);
    try {
      let taskId;
      if (taskModal?.id) {
        const res = await updateTask(taskModal.id, form);
        setTasks(prev => prev.map(t => t.id === taskModal.id ? res.task : t));
        taskId = taskModal.id;
        showToast && showToast('✓ Task actualizat');
      } else {
        const res = await createTask(form);
        if ((res.task.proceduri_ids || []).includes(procId)) {
          setTasks(prev => [res.task, ...prev]);
        }
        taskId = res.task.id;
        showToast && showToast('✓ Task creat');
      }
      for (const p of pendingFiles) {
        const fd = new FormData();
        fd.append('file', p.file);
        fd.append('denumire', p.desc);
        await uploadTaskFile(taskId, fd).catch(() => {});
      }
      setTaskModal(undefined);
    } catch {
      showToast && showToast('Eroare la salvare task');
    } finally {
      setTaskSaving(false);
    }
  };

  const handleTaskDelete = async () => {
    if (!taskModal?.id || !window.confirm('Sigur vrei sa stergi acest task?')) return;
    setTaskDeleting(true);
    try {
      await deleteTask(taskModal.id);
      setTasks(prev => prev.filter(t => t.id !== taskModal.id));
      setTaskModal(undefined);
      showToast && showToast('Task sters');
    } catch {
      showToast && showToast('Eroare la stergere');
    } finally {
      setTaskDeleting(false);
    }
  };

  const set = (patch) => {
    const next = { ...r, ...patch };
    setR(next);
    onChange(next);
  };

  const setFaza = (cheie, patch) => {
    const next = { ...r, faze: { ...r.faze, [cheie]: { ...r.faze[cheie], ...patch } } };
    setR(next);
    onChange(next);
  };

  const status = useMemo(() => deriveStatus(r, cfg), [r, cfg]);
  const timbru = useMemo(() => estimeazaTimbru(r.valoare), [r.valoare]);

  const addTermen = () => set({ termene: [...(r.termene || []), { id: newId(), data:'', obiectTermen:'', nota:'' }] });
  const setTermen = (id, patch) => set({ termene: r.termene.map(t => t.id === id ? { ...t, ...patch } : t) });
  const delTermen = (id) => set({ termene: r.termene.filter(t => t.id !== id) });

  return (
    <>
    <div className="tracker">
      <AlertBanner status={status} />

      {/* Datele dosarului */}
      <TrackerCard title="Dosarul">
        <div className="f-row">
          <div className="field" style={{ flex: 2 }}>
            <label>Denumire litigiu</label>
            <input type="text" value={r.denumire} onChange={e => set({ denumire: e.target.value })} placeholder="ex: SC Alpha SRL vs SC Beta SRL — contrafacere marca" />
          </div>
          <div className="field">
            <label>Tipul litigiului</label>
            <FilterPill value={r.tipLitigiu} onChange={e => set({ tipLitigiu: e.target.value })}>
              {TIPURI_LITIGIU.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </FilterPill>
          </div>
        </div>
        <div className="f-row">
          <div className="field">
            <label>Adversar / Parat</label>
            <input type="text" value={r.adversar} onChange={e => set({ adversar: e.target.value })} />
          </div>
          <div className="field">
            <label>Pozitia noastra</label>
            <FilterPill value={r.pozitie} onChange={e => set({ pozitie: e.target.value })}>
              {POZITII.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </FilterPill>
          </div>
        </div>
        <div className="field">
          <label>Obiectul actiunii</label>
          <input type="text" value={r.obiect} onChange={e => set({ obiect: e.target.value })} placeholder="ex: Interzicerea folosirii semnului X, daune 50.000 lei" />
        </div>
        <div className="f-row">
          <div className="field">
            <label>Data deschiderii dosarului</label>
            <DateField value={r.dataDeschidere || ''} onChange={v => set({ dataDeschidere: v })} />
          </div>
          <div className="field">
            <label>Beneficiar</label>
            <input type="text" value={r.beneficiar || ''} onChange={e => set({ beneficiar: e.target.value })} placeholder="Beneficiarul serviciilor" />
          </div>
        </div>
        <div className="field">
          <label>Responsabil</label>
          <MultiSelectDropdown
            options={staffUsers}
            value={r.responsabil || []}
            onChange={v => set({ responsabil: v })}
            placeholder="Selectati responsabil…"
          />
        </div>
        <label className="check-label">
          <input type="checkbox" checked={r.inchis} onChange={e => set({ inchis: e.target.checked })} />
          Dosar inchis / finalizat
        </label>
      </TrackerCard>

      {/* Fazele procedurale */}
      {FAZE.map(f => {
        const fz = r.faze[f.cheie];
        const hasSolutie = !!fz.solutie;
        const comunicare = parseISO(fz.dataComunicare);
        const termenCA = hasSolutie && comunicare && !fz.caleAtacDeclarata
          ? addDays(comunicare, cfg.cale_atac_zile) : null;
        return (
          <TrackerCard key={f.cheie} title={`Faza de ${f.label}`}>
            <div className="f-row">
              <div className="field">
                <label>Nr. dosar</label>
                <input type="text" value={fz.numarDosar} onChange={e => setFaza(f.cheie, { numarDosar: e.target.value })} placeholder="12345/3/2026" className="mono" />
              </div>
              <div className="field">
                <label>Instanta</label>
                <FilterPill value={fz.instanta} onChange={e => setFaza(f.cheie, { instanta: e.target.value })}>
                  <option value="">— selectati —</option>
                  {INSTANTE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </FilterPill>
              </div>
              <div className="field">
                <label>Data inregistrarii</label>
                <DateField value={fz.dataInregistrare} onChange={v => setFaza(f.cheie, { dataInregistrare: v })} />
              </div>
            </div>
            <div className="f-row">
              <div className="field">
                <label>Solutia</label>
                <FilterPill value={fz.solutie} onChange={e => setFaza(f.cheie, { solutie: e.target.value })}>
                  {SOLUTII.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </FilterPill>
              </div>
              <div className="field">
                <label>Data pronuntarii</label>
                <DateField value={fz.dataPronuntare} onChange={v => setFaza(f.cheie, { dataPronuntare: v })} />
              </div>
              <div className="field">
                <label>Data comunicarii</label>
                <DateField value={fz.dataComunicare} onChange={v => setFaza(f.cheie, { dataComunicare: v })} />
                {termenCA && <span className="f-hint">Termen cale de atac: {fmtRO(termenCA)} ({daysLeft(termenCA)}z)</span>}
              </div>
            </div>
            {termenCA && <DeadlineChip label={`Termen cale de atac (${cfg.cale_atac_zile} zile)`} date={termenCA} act />}
            <label className="check-label mt4">
              <input type="checkbox" checked={fz.caleAtacDeclarata} onChange={e => setFaza(f.cheie, { caleAtacDeclarata: e.target.checked })} />
              Cale de atac declarata
            </label>
            {fz.caleAtacDeclarata && (
              <div className="field" style={{ marginLeft: 22 }}>
                <label>Data declararii caii de atac</label>
                <DateField value={fz.dataCaleAtac} onChange={v => setFaza(f.cheie, { dataCaleAtac: v })} />
              </div>
            )}
          </TrackerCard>
        );
      })}

      {/* Termene de judecata */}
      <TrackerCard title="Termene de judecata">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8 }}>
          <p className="f-hint" style={{ margin:0 }}>Toate termenele de judecata din toate gradele.</p>
          <button type="button" className="btn-link" onClick={addTermen}>+ Adauga termen</button>
        </div>
        {(r.termene || []).length === 0 ? (
          <p className="f-hint">Niciun termen inregistrat.</p>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap: 4 }}>
            {r.termene.map(t => {
              const d = parseISO(t.data);
              const trecut = d && d < todayMid();
              return (
                <div key={t.id} style={{ display:'grid', gridTemplateColumns:'auto 1fr 1fr auto', gap: 8, alignItems:'center', padding:'6px 8px', border:'1px solid var(--border)', borderRadius: 6, background: trecut ? 'var(--bg-subtle)' : 'transparent' }}>
                  <DateField value={t.data} onChange={v => setTermen(t.id, { data: v })} />
                  <input type="text" value={t.obiectTermen || ''} onChange={e => setTermen(t.id, { obiectTermen: e.target.value })}
                    placeholder="Obiectul termenului" style={{ fontSize: 12, border:'none', outline:'none', background:'transparent' }} />
                  <input type="text" value={t.nota} onChange={e => setTermen(t.id, { nota: e.target.value })}
                    placeholder="Ce s-a dispus…" style={{ fontSize: 12, border:'none', outline:'none', background:'transparent' }} />
                  <button type="button" className="btn-link" style={{ color:'var(--danger)' }} onClick={() => delTermen(t.id)}>✕</button>
                </div>
              );
            })}
          </div>
        )}
      </TrackerCard>

      <SedinteSyncCard sync={r.sedinte_sync} />

      {/* Masuri provizorii */}
      <TrackerCard title="Masuri provizorii si asiguratorii">
        <label className="check-label">
          <input type="checkbox" checked={r.masuriProvizorii} onChange={e => set({ masuriProvizorii: e.target.checked })} />
          Au fost solicitate sau acordate masuri provizorii / asiguratorii
        </label>
        {r.masuriProvizorii && (
          <div className="dep-section">
            <div className="f-row">
              <div className="field">
                <label>Tipul masurii</label>
                <input type="text" value={r.masuriTip} onChange={e => set({ masuriTip: e.target.value })} placeholder="ex: suspendare comercializare, sechestru" />
              </div>
              <div className="field">
                <label>Data dispunerii</label>
                <DateField value={r.masuriData} onChange={v => set({ masuriData: v })} />
              </div>
            </div>
            <div className="field">
              <label>Stadiu / solutie</label>
              <input type="text" value={r.masuriSolutie} onChange={e => set({ masuriSolutie: e.target.value })} placeholder="ex: Tribunal admis, in executare" />
            </div>
          </div>
        )}
      </TrackerCard>

      {/* Expertiza */}
      <TrackerCard title="Expertiza tehnica sau contabila">
        <label className="check-label">
          <input type="checkbox" checked={r.expertizaDispusa} onChange={e => set({ expertizaDispusa: e.target.checked })} />
          Expertiza dispusa de instanta
        </label>
        {r.expertizaDispusa && (
          <div className="dep-section">
            <div className="f-row">
              <div className="field">
                <label>Expert</label>
                <input type="text" value={r.expert} onChange={e => set({ expert: e.target.value })} />
              </div>
              <div className="field">
                <label>Termen depunere raport</label>
                <DateField value={r.expertizaTermenRaport} onChange={v => set({ expertizaTermenRaport: v })} />
              </div>
            </div>
            <div className="field">
              <label>Obiective expertiza</label>
              <textarea value={r.expertizaObiective} onChange={e => set({ expertizaObiective: e.target.value })}
                className="tr-textarea" rows={2} />
            </div>
            <label className="check-label">
              <input type="checkbox" checked={r.expertizaRaportDepus} onChange={e => set({ expertizaRaportDepus: e.target.checked })} />
              Raportul de expertiza a fost depus
            </label>
          </div>
        )}
      </TrackerCard>

      {/* Executare silita */}
      <TrackerCard title="Executare silita">
        <label className="check-label">
          <input type="checkbox" checked={r.executare} onChange={e => set({ executare: e.target.checked })} />
          S-a pornit executarea silita
        </label>
        {r.executare && (
          <div className="dep-section">
            <div className="f-row">
              <div className="field">
                <label>Nr. dosar executare</label>
                <input type="text" value={r.executareNumarDosar} onChange={e => set({ executareNumarDosar: e.target.value })} className="mono" />
              </div>
              <div className="field">
                <label>Stadiu executare</label>
                <input type="text" value={r.executareStadiu} onChange={e => set({ executareStadiu: e.target.value })} />
              </div>
            </div>
          </div>
        )}
      </TrackerCard>

      {/* Sarcini interne — powered by Tasks */}
      <TrackerCard title="Sarcini interne">
        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom: 8 }}>
          <button type="button" className="btn-link" onClick={() => setTaskModal(null)}>+ Adauga sarcina</button>
        </div>
        {tasks.length === 0 ? (
          <p className="f-hint">Nicio sarcina interna.</p>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap: 6 }}>
            {tasks.map(t => (
              <div
                key={t.id}
                onClick={() => setTaskModal(t)}
                style={{ display:'flex', alignItems:'center', gap: 10, border:'1px solid var(--border)', borderRadius: 8, padding:'9px 12px', cursor:'pointer', opacity: t.status === 'terminat' ? 0.55 : 1, transition:'background .1s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_DOT[t.status] || '#9ca3af', flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 13, fontWeight: 500, textDecoration: t.status === 'terminat' ? 'line-through' : 'none' }}>{t.nume}</span>
                {(t.data || t.termen_limita) && (
                  <span style={{ fontSize: 11, color:'var(--muted)', whiteSpace:'nowrap' }}>
                    {fmtDate(t.data || t.termen_limita)}
                  </span>
                )}
                <span style={{ fontSize: 11, color:'var(--muted)', background:'#f3f4f6', borderRadius: 4, padding:'2px 6px', whiteSpace:'nowrap' }}>
                  {STATUS_LABEL[t.status] || t.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </TrackerCard>

      {taskModal !== undefined && (
        <TaskModal
          task={taskModal}
          prefill={taskModal === null ? {
            clienti_ids: clientId ? [clientId] : [],
            proceduri_ids: procId ? [procId] : [],
          } : undefined}
          staffUsers={staffUsers}
          clienti={clienti}
          allProceduri={allProceduri}
          onSave={handleTaskSave}
          onDelete={handleTaskDelete}
          onClose={() => setTaskModal(undefined)}
          saving={taskSaving}
          deleting={taskDeleting}
        />
      )}

      {/* Financiar */}
      <TrackerCard title="Date financiare">
        <button type="button" className="btn-link mb8" onClick={() => setShowFinanciar(s => !s)}>
          {showFinanciar ? '▾' : '▸'} {showFinanciar ? 'Ascunde' : 'Afiseaza'} date financiare
        </button>
        {showFinanciar && (
          <>
            <div className="f-row">
              <div className="field">
                <label>Valoarea obiectului litigiului (lei)</label>
                <input type="number" min="0" value={r.valoare} onChange={e => set({ valoare: Number(e.target.value)||0 })} className="mono" />
                <span className="f-hint">Taxa timbru estimata (O.U.G. 80/2013): {fmtLei(timbru)}</span>
              </div>
              <div className="field">
                <label>Taxa timbru platita (lei)</label>
                <input type="number" min="0" value={r.taxaTimbru} onChange={e => set({ taxaTimbru: Number(e.target.value)||0 })} className="mono" />
              </div>
            </div>
            <label className="check-label">
              <input type="checkbox" checked={r.taxaPlatita} onChange={e => set({ taxaPlatita: e.target.checked })} />
              Taxa timbru achitata
            </label>
            <div className="tr-divider mt8">Onorariu</div>
            <div className="f-row">
              <div className="field">
                <label>Onorariu contractat (lei)</label>
                <input type="number" min="0" value={r.onorariu} onChange={e => set({ onorariu: Number(e.target.value)||0 })} className="mono" />
              </div>
              <div className="field">
                <label>Onorariu incasat (lei)</label>
                <input type="number" min="0" value={r.onorariuIncasat} onChange={e => set({ onorariuIncasat: Number(e.target.value)||0 })} className="mono" />
              </div>
            </div>
            <div className="f-row">
              <div className="field">
                <label>Cheltuieli avansate (lei)</label>
                <input type="number" min="0" value={r.cheltuieliAvansate} onChange={e => set({ cheltuieliAvansate: Number(e.target.value)||0 })} className="mono" />
              </div>
              <div className="field">
                <label>Cheltuieli recuperate (lei)</label>
                <input type="number" min="0" value={r.cheltuieliRecuperate} onChange={e => set({ cheltuieliRecuperate: Number(e.target.value)||0 })} className="mono" />
              </div>
            </div>
          </>
        )}
        <button type="button" className="btn-link mt8" onClick={() => setShowSetari(s => !s)}>
          {showSetari ? '▾' : '▸'} Configuratie termene
        </button>
        {showSetari && (
          <div className="tarife-editor">
            <label className="tarife-field">Termen cale de atac (zile)
              <input type="number" value={cfg.cale_atac_zile} onChange={e => setCfg({ ...cfg, cale_atac_zile: Number(e.target.value)||0 })} />
            </label>
            <label className="tarife-field">Alerta urgenta (zile)
              <input type="number" value={cfg.alerta_zile} onChange={e => setCfg({ ...cfg, alerta_zile: Number(e.target.value)||0 })} />
            </label>
          </div>
        )}
      </TrackerCard>

      <TrackerCard title="Observatii interne">
        {r.detaliiInterne !== undefined && (
          <div className="field">
            <label>Detalii interne</label>
            <textarea className="tr-textarea" value={r.detaliiInterne || ''} onChange={e => set({ detaliiInterne: e.target.value })}
              rows={2} placeholder="Detalii din istoricul dosarului…" />
          </div>
        )}
        <textarea className="tr-textarea" value={r.observatii} onChange={e => set({ observatii: e.target.value })}
          rows={3} placeholder="Pozitia partilor, argumente cheie, note strategice…" />
      </TrackerCard>

      

      <TrackerCard title="Fisiere dosar">
        <ProceduraFileSection
          procId={procId}
          showToast={showToast}
          pendingFiles={pendingFiles}
          onPendingFilesChange={onPendingFilesChange}
        />
      </TrackerCard>

      <TrackerCard title="Genereaza Documente">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <button type="button" className="btn btn-secondary" onClick={openActivModal}>
            Activitati potential facturabile
          </button>
          <button type="button" className="btn btn-secondary" onClick={openJuriModal}>
            Jurisprudenta
          </button>
          {LITIGIU_DOC_TYPES.map(([prompt, title]) => (
            <button
              key={prompt}
              type="button"
              className="btn btn-secondary"
              onClick={() => openDocModal(prompt, title)}
            >
              {title}
            </button>
          ))}
        </div>
      </TrackerCard>
    </div>

    {activOpen && (
      <div className="modal-overlay">
        <div className="modal-box" style={{ maxWidth: 560 }}>
          <div className="modal-sticky-header" style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: 16 }}>Activitati potential facturabile</span>
            <button type="button" className="btn-link" style={{ marginLeft: 'auto' }} onClick={() => setActivOpen(false)}>✕</button>
          </div>
          <div className="modal-scroll-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="field">
              <label>Instructiuni suplimentare</label>
              <textarea
                className="tr-textarea"
                rows={4}
                placeholder="Descrie particularitati ale dosarului, activitati deja realizate care nu mai trebuie incluse, etape suplimentare sau orice alt context relevant (in romana)..."
                value={activText}
                onChange={e => setActivText(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setActivOpen(false)} disabled={activGenerating}>
                Anuleaza
              </button>
              <button type="button" className="btn btn-primary" onClick={handleGenerateActivitati} disabled={activGenerating}>
                {activGenerating ? 'Se genereaza...' : 'Genereaza'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {juriOpen && (
      <div className="modal-overlay">
        <div className="modal-box" style={{ maxWidth: 560 }}>
          <div className="modal-sticky-header" style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: 16 }}>Genereaza jurisprudenta</span>
            <button type="button" className="btn-link" style={{ marginLeft: 'auto' }} onClick={() => setJuriOpen(false)}>✕</button>
          </div>
          <div className="modal-scroll-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            <div className="field">
              <label>Informatii suplimentare</label>
              <textarea
                className="tr-textarea"
                rows={4}
                placeholder="Descrie aspectele specifice ale cazului, argumente cheie, ce tip de jurisprudenta ai nevoie (ex: dovada utilizarii, risc de confuzie, rea-credinta)..."
                value={juriText}
                onChange={e => setJuriText(e.target.value)}
              />
            </div>

            <div className="field">
              <label>Fisiere dosar de inclus in analiza</label>
              {juriFiles.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0 }}>
                  Niciun fisier incarcat pentru acest dosar.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                  {juriFiles.map(f => (
                    <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={juriSelected.has(f.id)}
                        onChange={e => {
                          setJuriSelected(prev => {
                            const next = new Set(prev);
                            e.target.checked ? next.add(f.id) : next.delete(f.id);
                            return next;
                          });
                        }}
                      />
                      <span style={{ fontWeight: 500 }}>{f.denumire || f.filename}</span>
                      {f.denumire && f.denumire !== f.filename && (
                        <span style={{ color: 'var(--muted)', fontSize: 11 }}>{f.filename}</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setJuriOpen(false)} disabled={juriGenerating}>
                Anuleaza
              </button>
              <button type="button" className="btn btn-primary" onClick={handleGenerateJurisprudenta} disabled={juriGenerating}>
                {juriGenerating ? 'Se genereaza...' : 'Genereaza'}
              </button>
            </div>

          </div>
        </div>
      </div>
    )}

    {docModal && (
      <div className="modal-overlay">
        <div className="modal-box" style={{ maxWidth: 560 }}>
          <div className="modal-sticky-header" style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: 16 }}>{docModal.title}</span>
            <button type="button" className="btn-link" style={{ marginLeft: 'auto' }} onClick={() => setDocModal(null)}>✕</button>
          </div>
          <div className="modal-scroll-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            <div className="field">
              <label>Instructiuni suplimentare</label>
              <textarea
                className="tr-textarea"
                rows={4}
                placeholder="Instructiuni in romana pentru Claude: pozitia clientului, argumente cheie, temeiuri de invocat, aspecte de evitat, date concrete de folosit in document..."
                value={docText}
                onChange={e => setDocText(e.target.value)}
              />
            </div>

            <div className="field">
              <label>Fisiere dosar de inclus in analiza</label>
              {docFiles.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0 }}>
                  Niciun fisier incarcat pentru acest dosar.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                  {docFiles.map(f => (
                    <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={docSelected.has(f.id)}
                        onChange={e => {
                          setDocSelected(prev => {
                            const next = new Set(prev);
                            e.target.checked ? next.add(f.id) : next.delete(f.id);
                            return next;
                          });
                        }}
                      />
                      <span style={{ fontWeight: 500 }}>{f.denumire || f.filename}</span>
                      {f.denumire && f.denumire !== f.filename && (
                        <span style={{ color: 'var(--muted)', fontSize: 11 }}>{f.filename}</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setDocModal(null)} disabled={docGenerating}>
                Anuleaza
              </button>
              <button type="button" className="btn btn-primary" onClick={handleGenerateDoc} disabled={docGenerating}>
                {docGenerating ? 'Se genereaza...' : 'Genereaza'}
              </button>
            </div>

          </div>
        </div>
      </div>
    )}
    </>
  );
}

/* ── Sub-components ── */
function AlertBanner({ status }) {
  const { alert } = status;
  return (
    <div className={`tr-alert sev-${alert.sev}`}>
      <strong>{alert.label}</strong>
      {alert.date && (
        <span className="tr-alert-date">
          Termen: {fmtRO(alert.date)}
          {typeof alert.daysLeft === 'number' && (
            alert.daysLeft < 0 ? ` · depasit cu ${Math.abs(alert.daysLeft)} zile` : ` · ${alert.daysLeft} zile ramase`
          )}
        </span>
      )}
    </div>
  );
}

function TrackerCard({ title, badge, children }) {
  return (
    <div className="tr-card">
      <div className="tr-card-head">
        <span className="tr-card-title">{title}</span>
        {badge && <span className="tr-badge">{badge}</span>}
      </div>
      <div className="tr-card-body">{children}</div>
    </div>
  );
}

function MultiSelectDropdown({ options, value = [], onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, minWidth: 200 });
  const btnRef = useRef(null);
  const dropRef = useRef(null);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, minWidth: Math.max(r.width, 200) });
    }
    setOpen(o => !o);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current?.contains(e.target) || dropRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (name) => onChange(
    value.includes(name) ? value.filter(n => n !== name) : [...value, name]
  );

  return (
    <div style={{ display:'flex', alignItems:'center', gap: 6, flexWrap:'wrap' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        style={{ fontSize:12, padding:'4px 10px', border:'1px solid var(--border)', borderRadius: 6, background:'var(--bg)', cursor:'pointer', whiteSpace:'nowrap' }}
      >
        {placeholder} {open ? '▴' : '▾'}
      </button>

      {open && (
        <div ref={dropRef} style={{ position:'fixed', top: pos.top, left: pos.left, minWidth: pos.minWidth, maxHeight: 240, overflowY:'auto', zIndex:9999, background:'var(--bg)', border:'1px solid var(--border)', borderRadius: 6, padding:'4px 0', boxShadow:'0 4px 16px rgba(0,0,0,0.15)' }}>
          {options.length === 0 && (
            <div style={{ padding:'8px 12px', fontSize:13, color:'var(--muted)' }}>Niciun utilizator</div>
          )}
          {options.map(u => (
            <label key={u.id} style={{ display:'flex', alignItems:'center', gap: 8, padding:'6px 12px', cursor:'pointer', fontSize:13, userSelect:'none' }}
              onMouseEnter={e => e.currentTarget.style.background='var(--bg-subtle)'}
              onMouseLeave={e => e.currentTarget.style.background=''}
            >
              <input type="checkbox" checked={value.includes(u.full_name)} onChange={() => toggle(u.full_name)} style={{ flexShrink:0, margin:0 }} />
              <span>{u.full_name}</span>
            </label>
          ))}
        </div>
      )}

      {value.map(name => (
        <span key={name} style={{ display:'inline-flex', alignItems:'center', gap: 4, padding:'2px 8px', background:'var(--bg-subtle)', border:'1px solid var(--border)', borderRadius:12, fontSize:12 }}>
          {name}
          <button type="button" onClick={() => toggle(name)}
            style={{ border:'none', background:'none', cursor:'pointer', color:'var(--muted)', padding:0, lineHeight:1, fontSize:13 }}>×</button>
        </span>
      ))}
    </div>
  );
}

function SedinteSyncCard({ sync }) {
  const [expanded, setExpanded] = useState({});
  const toggle = useCallback((i) => setExpanded(prev => ({ ...prev, [i]: !prev[i] })), []);

  if (!sync || !Array.isArray(sync.sedinte) || sync.sedinte.length === 0) return null;

  const syncedAt = sync.synced_at
    ? new Date(sync.synced_at).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <TrackerCard title="Sedinte (sincronizat din portal)" badge={sync.sedinte.length}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        {sync.institutie && <span className="f-hint" style={{ margin: 0 }}>{sync.institutie}</span>}
        {sync.stadiuProcesual && <span className="f-hint" style={{ margin: 0 }}>· {sync.stadiuProcesual}</span>}
        {syncedAt && <span className="f-hint" style={{ margin: 0, marginLeft: 'auto' }}>Sincronizat {syncedAt}</span>}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {sync.sedinte.map((s, i) => {
          const isOpen = !!expanded[i];
          const hasSumar = !!s.solutieSumar;
          return (
            <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              <div
                style={{ display: 'grid', gridTemplateColumns: '110px 70px 1fr auto', gap: 8, alignItems: 'center', padding: '7px 10px', background: 'var(--bg-subtle)', cursor: hasSumar ? 'pointer' : 'default' }}
                onClick={() => hasSumar && toggle(i)}
              >
                <span style={{ fontSize: 12, fontWeight: 600 }}>{s.data || '—'}</span>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>{s.ora || ''}{s.complet ? ` · ${s.complet}` : ''}</span>
                <span style={{ fontSize: 12 }}>{s.solutie || '—'}</span>
                {hasSumar && (
                  <span style={{ fontSize: 11, color: 'var(--muted)', userSelect: 'none' }}>{isOpen ? '▾' : '▸'}</span>
                )}
              </div>
              {isOpen && hasSumar && (
                <div style={{ padding: '8px 10px', fontSize: 12, lineHeight: 1.6, whiteSpace: 'pre-wrap', borderTop: '1px solid var(--border)' }}>
                  {s.solutieSumar}
                  {(s.document || s.numarDocument) && (
                    <div style={{ marginTop: 6, color: 'var(--muted)', fontSize: 11 }}>
                      Document: {[s.document, s.numarDocument, s.dataDocument].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </TrackerCard>
  );
}

function DeadlineChip({ label, date, act }) {
  if (!date) return null;
  const dl = daysLeft(date);
  const overdue = dl < 0;
  const soon = dl >= 0 && dl <= 14;
  const cls = overdue ? 'chip-overdue' : (act && soon) ? 'chip-act' : 'chip-neutral';
  return (
    <div className={`deadline-chip ${cls}`}>
      <span>{label}</span>
      <span>{fmtRO(date)} · {overdue ? `${Math.abs(dl)}z depasit` : `${dl}z`}</span>
    </div>
  );
}
